import React, { useState, useRef, useEffect } from "react";
import { Card, Input, Label, Table } from "reactstrap";
import { PlusCircle, SearchIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { deleteParameter, getParameter, statusParameter } from "./service/parameter-service";
import MyPagination from "../../pagination";
import Swal from "sweetalert2";
import { useLoader } from "../../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { FormLabel } from "react-bootstrap";
import { Tooltip } from "react-tooltip";

export default function HomeReport() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const { setLoading } = useLoader();

    useEffect(() => {
        getParamete();
    }, [currentPage, entriesPerPage]);

    const getParamete = async () => {
        setLoading(true);
        try {
            const form_data = new FormData();
            form_data.append("page", currentPage);
            form_data.append("limit", entriesPerPage);
            form_data.append("search", search);
            const res = await getParameter(form_data);
            if (res.data.success === true) {
                setUsers(res.data.data.data);
                setTotalEntries(res.data.data.total || 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // Debounced search
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setCurrentPage(1); // Reset to first page on search
            getParamete();
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [search]);

    const totalPages = Math.ceil(totalEntries / entriesPerPage);

    const handleToggleStatus = (data) => {
        Swal.fire({
            title: "Are you sure?",
            text: data.isActive
                ? "You want to inactivate this parameter?"
                : "You want to activate this parameter?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: data.isActive ? "Inactive" : "Active"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await statusParameter(data._id);
                    if (res.data.success == true) {
                        toast.success(res.data.message);
                        getParamete();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleEdit = (data) => {
        navigate(`${process.env.PUBLIC_URL}/edit-parameter/${data._id}`);
    };

    const detele = (data) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this parameter?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await deleteParameter(data._id);
                    if (res.data.success == true) {
                        toast.success(res.data.message);
                        getParamete();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setEntriesPerPage(newSize);
        setCurrentPage(1);
    };

    const getStartingSerialNumber = () => {
        return (currentPage - 1) * entriesPerPage + 1;
    };

    // Helper to format a single range value
    const formatRangeValue = (range) => {
        if (range.minValue != null && range.maxValue != null) {
            return `${range.minValue} - ${range.maxValue}`;
        }
        return range.qualitativeValue || range.ratioValue || range.scaleValue || "N/A";
    };

    // Show all reference ranges in a popup
    const showAllRanges = (ranges) => {
        const rangeList = ranges
            .map((r, idx) => {
                const value = formatRangeValue(r);
                const gender = r.gender === "all" ? "All" : r.gender.charAt(0).toUpperCase() + r.gender.slice(1);
                const ageCategory = r.ageCategory
                    ? r.ageCategory.charAt(0).toUpperCase() + r.ageCategory.slice(1)
                    : "N/A";

                return `
                    <div class="tw-text-left tw-mb-3 tw-p-3 tw-bg-gray-50 tw-rounded">
                        <strong>Range ${idx + 1}:</strong> ${value}<br/>
                        <small class="tw-text-gray-600">
                            Gender: ${gender} | Age Category: ${ageCategory}
                        </small>
                    </div>
                `;
            })
            .join("<hr class='tw-my-3'/>");

        Swal.fire({
            title: `<strong>All Reference Ranges</strong>`,
            html: rangeList || "<div>No ranges defined</div>",
            // icon: "info",
            confirmButtonText: "Close",
            width: "600px",
            customClass: {
                popup: "swal-custom-popup"
            }
        });
    };

    // Format display for Reference Ranges column
    const formatReferenceRangeDisplay = (ranges) => {
        if (!ranges || ranges.length === 0) return "N/A";

        const firstRangeValue = formatRangeValue(ranges[0]);

        if (ranges.length === 1) {
            return <span>{firstRangeValue}</span>;
        }

        return (
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <span>{firstRangeValue}</span>
                <button
                    className="tw-cursor-pointer tw-border tw-text-black tw-border-[#006E27] tw-rounded tw-px-2 tw-py-1"
                    onClick={(e) => {
                        e.stopPropagation();
                        showAllRanges(ranges);
                    }}
                >
                    +{ranges.length - 1} more
                </button>
            </div>
        );
    };

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="input-group mb-3">
                                <span className="input-group-text tw-bg-white" id="basic-addon1">
                                    <SearchIcon />
                                </span>
                                <input
                                    type="text"
                                    className="form-control icon_form"
                                    placeholder="Search By Code/Name..."
                                    aria-label="Search"
                                    aria-describedby="basic-addon1"
                                    style={{ borderLeft: 'none' }}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-6 text-end">
                            <Link to={`${process.env.PUBLIC_URL}/add-parameter`}>
                                <button className="btn btn-primary">
                                    {t("Add")}
                                </button>
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <div className="recent-table table-responsive currency-table tw-rounded-lg tw-border tw-border-black">
                                <Table className="mb-0 table-center table-bordered table-hover" style={{ tableLayout: "fixed" }}>
                                    <thead>
                                        <tr>
                                            <th className="text-center bg-primary tw-text-center" style={{ width: "80px" }}><b>#</b></th>
                                            <th className="bg-primary text-white" style={{ width: "120px" }}><b>Code</b></th>
                                            <th className="bg-primary text-white" style={{ width: "150px" }}><b>Name</b></th>
                                            <th className="bg-primary text-white" style={{ width: "160px" }}><b>Measurement Type</b></th>
                                            <th className="bg-primary text-white" style={{ width: "200px" }}><b>Reference Ranges</b></th>
                                            <th className="bg-primary text-white" style={{ width: "100px" }}><b>Unit</b></th>
                                            <th className="bg-primary text-white tw-text-center" style={{ width: "80px" }}><b>Status</b></th>
                                            <th className="bg-primary text-white tw-text-center" style={{ width: "100px" }}><b>Action</b></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length > 0 ? (
                                            users.map((item, index) => {
                                                const serialNumber = getStartingSerialNumber() + index;
                                                return (
                                                    <tr key={item._id}>
                                                        <td className="text-center font-weight-bold">{serialNumber}</td>
                                                        <td className="ps-2">{item.code}</td>
                                                        <td>{item.name}</td>
                                                        <td>{item.measurementType}</td>
                                                        <td className="tw-py-2">
                                                            {formatReferenceRangeDisplay(item.referenceRanges)}
                                                        </td>
                                                        <td>{item.unitValue || "-"}</td>
                                                        <td className="text-center">
                                                            <Label className="switch">
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={item.isActive === true}
                                                                    onChange={() => handleToggleStatus(item)}
                                                                />
                                                                <span className="switch-state"></span>
                                                            </Label>
                                                        </td>
                                                        <td className="tw-text-center">
                                                            <div className="tw-flex tw-justify-center tw-items-center tw-gap-2">
                                                                <Tooltip id="edit" place="top" content="Edit" />
                                                                <button
                                                                    className="success-action-button"
                                                                    data-tooltip-id="edit"
                                                                    onClick={() => handleEdit(item)}
                                                                >
                                                                    <i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
                                                                </button>

                                                                <Tooltip id="delete" place="top" content="Delete" />
                                                                <button
                                                                    className="danger-action-button"
                                                                    data-tooltip-id="delete"
                                                                    onClick={() => detele(item)}
                                                                >
                                                                    <i className="fa fa-trash tw-text-red-600" style={{ fontSize: "18px" }}></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={8} className="text-center py-4 text-muted">
                                                    No data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-md-flex align-items-center text-center justify-content-between mt-3 mb-3">
                                <div className="show-entries">
                                    <FormLabel>Show&nbsp;</FormLabel>
                                    <select
                                        style={{ display: 'unset', width: 'auto', padding: '0.375rem 2.25rem 0.375rem 0.75rem' }}
                                        value={entriesPerPage}
                                        onChange={handlePageSizeChange}
                                        className="form-control"
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={30}>30</option>
                                        <option value={50}>50</option>
                                    </select>&nbsp;
                                    <FormLabel>entries</FormLabel>
                                </div>
                                <MyPagination
                                    totalPages={totalPages}
                                    currentPage={currentPage}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </>
    );
}